import { ExclamationCircleOutlined } from '@ant-design/icons'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useQueryClient } from 'react-query'
import { useNavigate } from 'react-router'
import { useConfirm } from 'sutra/components/confirm/use-confirm'

import { useRegisterBootstrapLoading } from 'components'
import { AuthContext, useSignoutConfirmation } from 'features/auth'
import { useProjectStore } from 'features/project/store'
import { HomeView } from 'features/projects/components/HomeView'
import { useBulkUpdateProjects } from 'features/projects/hooks/use-bulk-update-projects'
import { useDebouncedSearch } from 'features/projects/hooks/use-debounced-search'
import { useDeleteProject } from 'features/projects/hooks/use-delete-project'
import { useGroupProjects } from 'features/projects/hooks/use-group-projects'
import { useProjectMeta } from 'features/projects/hooks/use-project-meta'
import { useProjectSearch } from 'features/projects/hooks/use-project-search'
import { useHomeService } from 'features/projects/service'
import { projectFiltersStorageService } from 'features/projects/service/project-filters-storage'
import { BulkUpdatePayload } from 'features/projects/service/service'
import { Project } from 'features/projects/types'
import {
  applyProjectFilters,
  ProjectFilters,
} from 'features/projects/utils/project-filters'
import { projectPath } from 'routes/builders'
import { Role } from 'types/hub-role'
import { ProjectState } from 'types/project'

const DEFAULT_PAGE_SIZE = 25

/** Sort projects by title ascending — mirroring the table's defaultSortOrder. */
const seedTableRows = (rows: Project[]): Project[] =>
  [...rows].sort((a, b) => a.title.localeCompare(b.title))

type HomeProps = {
  renderProjectNewForm: (args: {
    reservedKeywords: string[]
    onClose: () => void
  }) => React.ReactNode
}

export const Home: React.FC<HomeProps> = ({ renderProjectNewForm }) => {
  const reset = useProjectStore((state: ProjectState) => state.reset)
  const queryClient = useQueryClient()

  // Reset global project state when arriving at home page (must run in effect, not during render)
  useEffect(() => {
    reset()
    queryClient.resetQueries({ queryKey: ['project'] })
  }, [reset, queryClient])

  // Service (Layer 1) — injected into all hooks
  const service = useHomeService()

  // Auth context — read here, pass as props to view
  const authContext = useContext(AuthContext)
  const isAdmin = authContext?.userDetails?.role === 'admin'
  const isOwner =
    authContext?.userDetails?.yogi_user === true &&
    authContext.userDetails.user_role === Role.Owner
  const confirmSignout = useSignoutConfirmation()

  // Hooks (Layer 2) — all logic lives here
  const search = useDebouncedSearch()
  const {
    data: projectData,
    isLoading: projectsLoading,
    error: projectsError,
    refetch: refetchProjects,
  } = useProjectSearch(
    service,
    search.searchType,
    search.debouncedTerm,
  ) as import('react-query').UseQueryResult<{ data: Project[] }>
  const { data: groupProjectData, isLoading: groupProjectsLoading } =
    useGroupProjects(service) as import('react-query').UseQueryResult<{
      data: {
        projects: Array<{ uuid: string; title: string; grouped?: boolean }>
      }
    }>
  const { data: metaData } = useProjectMeta(
    service,
  ) as import('react-query').UseQueryResult<{
    data: { reserved_keys: string[] }
  }>
  const deleteProject = useDeleteProject(service)
  const bulkUpdate = useBulkUpdateProjects(service)

  // Local UI state
  const [modalOpened, setModalOpened] = useState(false)
  const [showRelativeTime, setShowRelativeTime] = useState(false)
  const [projectFilters, setProjectFilters] = useState<ProjectFilters>(() =>
    projectFiltersStorageService.load(),
  )
  const [filtersModalOpen, setFiltersModalOpen] = useState(false)

  // Bulk edit state
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])
  // null = closed; populated = modal open with these projects
  const [editTarget, setEditTarget] = useState<Project[] | null>(null)
  // 'page' = page-local selection; 'all' = all filtered projects selected
  const [selectionScope, setSelectionScope] = useState<'page' | 'all'>('page')

  // Pagination state (controlled so keyboard nav can change pages)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  // Raw projects from the server
  const projects = useMemo(() => projectData?.data ?? [], [projectData])

  // Filtered project list driven by the filters modal
  const displayedProjects = useMemo(
    () => applyProjectFilters(projects, projectFilters),
    [projects, projectFilters],
  )

  // Table-visible-data — seeded from displayedProjects; overwritten by AntD onChange.
  // Re-seeds whenever displayedProjects changes (initial load, search change, toggle flip).
  const [tableRows, setTableRows] = useState<Project[]>([])
  useEffect(() => {
    setTableRows(seedTableRows(displayedProjects))
  }, [displayedProjects])

  // Keyboard navigation state
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null)

  // Reset pagination + highlight when search term or filters change
  const searchTerm = search.searchTerm
  useEffect(() => {
    setCurrentPage(1)
    setHighlightedIndex(null)
  }, [searchTerm, projectFilters])

  const navigate = useNavigate()

  // Side effect: redirect non-yogi users to grouped project
  useEffect(() => {
    if (
      groupProjectData?.data?.projects?.length &&
      authContext?.userDetails &&
      !authContext.userDetails.yogi_user &&
      !authContext.userDetails.email.includes('@churchdwight.com')
    ) {
      navigate(
        projectPath(
          groupProjectData.data.projects.find((el) => el.grouped)?.uuid ??
            groupProjectData.data.projects[0]!.uuid,
        ),
      )
    }
  }, [groupProjectData, authContext.userDetails, navigate])

  const confirm = useConfirm()

  // Side effect: delete with confirmation modal
  const handleDelete = useCallback(
    (uuid: string) => {
      void confirm({
        title: 'Do you want to delete this project?',
        icon: <ExclamationCircleOutlined />,
        onOk: async () => {
          await deleteProject.mutateAsync(uuid)
        },
      })
    },
    [confirm, deleteProject],
  )

  const handleProjectFiltersChange = useCallback((filters: ProjectFilters) => {
    setProjectFilters(filters)
    projectFiltersStorageService.save(filters)
  }, [])

  const handleTableChange = useCallback((currentDataSource: Project[]) => {
    setTableRows(currentDataSource)
  }, [])

  const handlePageChange = useCallback((page: number, size: number) => {
    setCurrentPage(page)
    setPageSize(size)
  }, [])

  // Bulk edit handlers
  const handleSelectedChange = useCallback(
    (keys: string[]) => {
      setSelectedRowKeys(keys)
      // Revert to page scope when the user manually adjusts selection
      if (selectionScope === 'all') {
        setSelectionScope('page')
      }
    },
    [selectionScope],
  )

  const handleSelectAll = useCallback(() => {
    setSelectedRowKeys(displayedProjects.map((p) => p.uuid))
    setSelectionScope('all')
  }, [displayedProjects])

  const handleOpenEditModal = useCallback(() => {
    const targetUuids =
      selectionScope === 'all'
        ? displayedProjects.map((p) => p.uuid)
        : selectedRowKeys
    const targets = displayedProjects.filter((p) =>
      targetUuids.includes(p.uuid),
    )
    setEditTarget(targets)
  }, [selectionScope, displayedProjects, selectedRowKeys])

  const handleCloseEditModal = useCallback(() => {
    setEditTarget(null)
  }, [])

  const handleEditFromMeatballs = useCallback((project: Project) => {
    setEditTarget([project])
  }, [])

  const handleArchiveProject = useCallback(
    (uuid: string) => {
      bulkUpdate.mutate({ proj_uuids: [uuid], body: { archived: true } })
    },
    [bulkUpdate],
  )

  const handleEditProjects = useCallback(
    async (patch: BulkUpdatePayload['body']): Promise<void> => {
      if (!editTarget || editTarget.length === 0) return
      await bulkUpdate.mutateAsync({
        proj_uuids: editTarget.map((p) => p.uuid),
        body: patch,
      })
      setEditTarget(null)
      setSelectedRowKeys([])
      setSelectionScope('page')
    },
    [editTarget, bulkUpdate],
  )

  const handleInlineToggleInternal = useCallback(
    (uuid: string, value: boolean) => {
      bulkUpdate.mutate({ proj_uuids: [uuid], body: { is_internal: value } })
    },
    [bulkUpdate],
  )

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlightedIndex((prev) => {
          const next =
            prev == null ? 0 : Math.min(prev + 1, tableRows.length - 1)
          const targetPage = Math.floor(next / pageSize) + 1
          if (targetPage !== currentPage) {
            setCurrentPage(targetPage)
          }
          return next
        })
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlightedIndex((prev) => {
          const next = prev == null ? 0 : Math.max(prev - 1, 0)
          const targetPage = Math.floor(next / pageSize) + 1
          if (targetPage !== currentPage) {
            setCurrentPage(targetPage)
          }
          return next
        })
      } else if (e.key === 'Enter') {
        if (highlightedIndex != null && tableRows[highlightedIndex]) {
          navigate(projectPath(tableRows[highlightedIndex].uuid))
        }
      }
    },
    [highlightedIndex, tableRows, pageSize, currentPage, navigate],
  )

  // The latch ensures later search refetches only affect the inline Table
  // spinner — not the full-page loader — so the search box stays mounted.
  const initialLoadComplete = useRef(false)
  if (!groupProjectsLoading && !projectsLoading) {
    initialLoadComplete.current = true
  }

  const isBootstrapping =
    !initialLoadComplete.current && (groupProjectsLoading || projectsLoading)

  // Register into the global overlay; must be called before any early return.
  useRegisterBootstrapLoading('projects-home', isBootstrapping)

  if (isBootstrapping) return null

  // Layer 3 → Layer 4: pass everything as props
  return (
    <HomeView
      projects={displayedProjects}
      isLoading={projectsLoading}
      error={projectsError as Error | null}
      onRetry={() => void refetchProjects()}
      isAdmin={isAdmin}
      isOwner={isOwner}
      searchType={search.searchType}
      searchTerm={search.searchTerm}
      onSearchTypeChange={search.setSearchType}
      onSearchTermChange={search.setSearchTerm}
      onSearchKeyDown={handleSearchKeyDown}
      onDeleteProject={handleDelete}
      onEditProjects={handleEditProjects}
      onEditProject={handleEditFromMeatballs}
      onArchiveProject={handleArchiveProject}
      onInlineToggleInternal={handleInlineToggleInternal}
      onSignOut={confirmSignout}
      onOpenAdmin={() => navigate('/admin')}
      modalOpened={modalOpened}
      onOpenModal={() => setModalOpened(true)}
      newProjectForm={renderProjectNewForm({
        reservedKeywords: metaData?.data?.reserved_keys ?? [],
        onClose: () => setModalOpened(false),
      })}
      highlightedIndex={highlightedIndex}
      currentPage={currentPage}
      pageSize={pageSize}
      onPageChange={handlePageChange}
      onTableChange={handleTableChange}
      showRelativeTime={showRelativeTime}
      onShowRelativeTimeChange={setShowRelativeTime}
      selectedRowKeys={selectedRowKeys}
      onSelectedChange={handleSelectedChange}
      editTarget={editTarget}
      onCloseEditModal={handleCloseEditModal}
      onOpenEditModal={handleOpenEditModal}
      totalFilteredCount={displayedProjects.length}
      isEditLoading={bulkUpdate.isLoading}
      onSelectAll={handleSelectAll}
      projectFilters={projectFilters}
      onProjectFiltersChange={handleProjectFiltersChange}
      filtersModalOpen={filtersModalOpen}
      onOpenFiltersModal={() => setFiltersModalOpen(true)}
      onCloseFiltersModal={() => setFiltersModalOpen(false)}
    />
  )
}
